<template>
  <div>
    <ApprovalFlowSkeleton v-if="isLoading" />

    <div
      v-else-if="isError"
      class="flex flex-col items-center justify-center py-8 gap-3"
      data-testid="approval-flow-load-error"
    >
      <p class="text-red-500 text-sm">
        Não foi possível carregar as configurações de excepcionalidade.
      </p>
      <outlined-button text="Tentar novamente" width="160px" height="36px" @click="loadFlow" />
    </div>

    <div v-else>
      <!-- O título da aba já vem do EditScheduleModal; aqui ficam só as ações. -->
      <div class="flex items-center justify-end mb-6">
        <div class="flex gap-2">
          <OutlinedButton
            text="Salvar como modelo"
            width="auto"
            height="36px"
            data-testid="save-template-button"
            @click="isSaveTemplateModalOpen = true"
          />
          <OutlinedButton
            text="Importar configurações"
            width="auto"
            height="36px"
            data-testid="import-exceptionality-button"
            @click="isImportModalOpen = true"
          />
        </div>
      </div>

      <ImportExceptionalityConfigModal
        ref="importModalRef"
        :modal-active="isImportModalOpen"
        :saved-template="savedTemplate"
        :schedule-id="props.scheduleId"
        :company-id="props.companyId"
        @close="handleImportClose"
        @save="handleImportConfig"
      />

      <SaveApprovalTemplateModal
        :modal-active="isSaveTemplateModalOpen"
        :layers="previewLayers"
        :loading="isSavingTemplate"
        @close="isSaveTemplateModalOpen = false"
        @save="handleSaveTemplate"
      />

      <div
        class="flex items-center gap-2 px-3 py-2 border border-primary30 rounded bg-primary5 mb-4"
        data-testid="info-operation-role"
      >
        <v-icon :icon="mdiFormTextboxLock" size="18" class="text-primary30 shrink-0" />
        <span class="text-sm text-secondary50">
          Os papéis validadores Operação e Análise de Risco são obrigatórios no fluxo de validação
          de excepcionalidade, e não podem ser deletados
        </span>
      </div>

      <div
        v-if="wasNormalizedOnLoad"
        class="flex items-center gap-2 px-3 py-2 border border-warning rounded bg-[#FEF9E7] mb-4"
        data-testid="alert-flow-normalized"
      >
        <v-icon :icon="mdiAlertOutline" size="18" class="text-warning shrink-0" />
        <span class="text-sm text-secondary50">
          Esta configuração foi atualizada para incluir os papéis obrigatórios. Salve para aplicar a
          mudança.
        </span>
      </div>

      <ApprovalLayerCard
        :group="approvalGroup"
        :show-errors="showErrors"
        @update:group="(updated) => (approvalGroup = updated)"
      />

      <ApprovalFlowPreview v-if="previewLayers.length > 0" :layers="previewLayers" class="mt-4" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted } from 'vue'
import { useToast } from 'vue-toastification'
import { useErrorToast } from '@/composables/useErrorToast'
import { mdiAlertOutline, mdiFormTextboxLock } from '@mdi/js'

import ApprovalLayerCard from '@/components/ManageScale/Schedule/components/ApprovalLayerCard.vue'
import ApprovalFlowPreview from '@/components/ManageScale/Schedule/ApprovalFlowPreview.vue'
import ApprovalFlowSkeleton from '@/components/designSystem/Skeleton/ApprovalFlowSkeleton.vue'
import OutlinedButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import ImportExceptionalityConfigModal from '@/components/designSystem/modals/ImportExceptionalityConfigModal/ImportExceptionalityConfigModal.vue'
import SaveApprovalTemplateModal from '@/components/designSystem/modals/SaveApprovalTemplateModal/SaveApprovalTemplateModal.vue'

import { useApprovalFlowStore } from '@/pinia-store/useApprovalFlowStore'
import type { ApprovalGroup } from '@/types/approval-flow.types'
import { LayerOperator } from '@/types/approval-flow.types'
import type { ApprovalLayerDetail } from '@/Network/Types/Responses/ApprovalFlowResponses'
import {
  apiLayersToGroup,
  createEmptyGroup,
  groupToApiLayers,
  allRulesHaveRoles,
  ensureMandatoryRoles
} from '@/utils/approval-flow-transformers'
import { useImportApprovalFlow } from '@/hooks/useImportApprovalFlow'

const props = defineProps<{ scheduleId: number; companyId?: number }>()
const emit = defineEmits<{ (e: 'update:isSaving', value: boolean): void }>()

const toast = useToast()
const { notifyError } = useErrorToast()
const approvalFlowStore = useApprovalFlowStore()
const { importConfig } = useImportApprovalFlow()

const approvalGroup = ref<ApprovalGroup>(createEmptyGroup())
const flowId = ref<number | null>(null)
const isLoading = ref(false)
const isError = ref(false)
const showErrors = ref(false)
const originalSnapshot = ref('')
// Fluxo legado corrigido na carga: some assim que o usuario salvar.
const wasNormalizedOnLoad = ref(false)
const importModalRef = ref<InstanceType<typeof ImportExceptionalityConfigModal> | null>(null)
const isImportModalOpen = ref(false)
const isSaveTemplateModalOpen = ref(false)
const isSavingTemplate = ref(false)
const savedTemplate = ref<{ id: number; name: string } | null>(null)

const handleImportConfig = async (payload: { source: 'template' | 'scale'; id: number }) => {
  const imported = await importConfig(payload)
  if (imported) {
    approvalGroup.value = imported
    isImportModalOpen.value = false
    importModalRef.value?.onImportSuccess()
  } else {
    isImportModalOpen.value = false
  }
  savedTemplate.value = null
}

const handleImportClose = () => {
  isImportModalOpen.value = false
  savedTemplate.value = null
}

const handleSaveTemplate = async (payload: { name: string; companyId: number }) => {
  isSavingTemplate.value = true
  try {
    const layers = groupToApiLayers(approvalGroup.value)
    const created = await approvalFlowStore.createTemplate({
      name: payload.name,
      companyId: payload.companyId,
      scheduleId: props.scheduleId,
      layers
    })
    savedTemplate.value = { id: created.id, name: created.name }
    isSaveTemplateModalOpen.value = false
    isImportModalOpen.value = true
    toast.success('Modelo salvo com sucesso.')
  } catch (error: unknown) {
    notifyError(error, 'Não foi possível salvar o modelo de configuração.')
  } finally {
    isSavingTemplate.value = false
  }
}

const previewLayers = computed<ApprovalLayerDetail[]>(() => {
  const apiLayers = groupToApiLayers(approvalGroup.value)
  return apiLayers.map((layer, idx) => ({
    id: idx,
    order: layer.order,
    operator: layer.operator ?? LayerOperator.AND,
    roles: (layer.roles ?? []).map((r, rIdx) => ({
      id: rIdx,
      role: r.role,
      weight: r.weight ?? rIdx + 1
    }))
  }))
})
const hasUnsavedChanges = computed(() => {
  if (!originalSnapshot.value) return false
  return JSON.stringify(approvalGroup.value) !== originalSnapshot.value
})

async function loadFlow() {
  isLoading.value = true
  isError.value = false

  try {
    await approvalFlowStore.fetchByScheduleId(props.scheduleId)

    if (approvalFlowStore.currentFlow) {
      flowId.value = approvalFlowStore.currentFlow.id

      // O snapshot tem de sair do grupo CRU, antes da normalização. Tirá-lo
      // depois zerava `hasUnsavedChanges`: a tela mostrava os papéis
      // obrigatórios, o banco continuava sem eles, o guard de saída não avisava
      // e a correção só existia enquanto o modal estivesse aberto.
      const persisted = apiLayersToGroup(approvalFlowStore.currentFlow.layers)
      originalSnapshot.value = JSON.stringify(persisted)
      approvalGroup.value = ensureMandatoryRoles(persisted)
      wasNormalizedOnLoad.value = hasUnsavedChanges.value
    } else {
      flowId.value = null
      approvalGroup.value = createEmptyGroup()
      originalSnapshot.value = JSON.stringify(approvalGroup.value)
      wasNormalizedOnLoad.value = false
    }
  } catch (error: unknown) {
    isError.value = true
    notifyError(error, 'Não foi possível carregar as configurações de excepcionalidade.')
  } finally {
    isLoading.value = false
  }
}

async function handleSave() {
  if (!allRulesHaveRoles(approvalGroup.value)) {
    showErrors.value = true
    toast.error('Todas as regras devem ter um papel validador.')
    return
  }

  showErrors.value = false
  emit('update:isSaving', true)

  try {
    const layers = groupToApiLayers(approvalGroup.value)

    if (flowId.value === null) {
      await approvalFlowStore.createFlow({ scheduleId: props.scheduleId, layers })
      flowId.value = approvalFlowStore.currentFlow?.id ?? null
    } else {
      await approvalFlowStore.updateFlow(flowId.value, { scheduleId: props.scheduleId, layers })
    }

    originalSnapshot.value = JSON.stringify(approvalGroup.value)
    wasNormalizedOnLoad.value = false
    toast.success('Excepcionalidade salva com sucesso.')
  } catch (error: unknown) {
    notifyError(error, 'Não foi possível salvar as configurações de excepcionalidade.')
  } finally {
    emit('update:isSaving', false)
  }
}

onMounted(() => loadFlow())

defineExpose({ handleSave, hasUnsavedChanges })
</script>
